import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';

import {
  getSchoolTerms,
  getGrades,
  getClassNos,
  getSubjects,
  getSyllabuses,
  getLessons,
  getLearningResultLessons,
} from 'GraphQL/queries/attendanceFilter.query';
import { SYLLABUS_MODE } from 'Domains/syllabus';

/**
 * Variables of foLearningResultLessons query.
 * The lesson filter and `useLessonName` use the same variables, so `useLessonName` reads
 * the lessons from Apollo cache instead of requesting them again.
 */
export const toLearningResultLessonsVariables = (
  { term, grade, subject, syllabus },
  schoolCode
) => ({
  schoolTermId: term,
  schoolGradeId: grade !== `all-grade-selected` ? grade : null,
  subjectId: subject,
  courseCode: syllabus !== `all-syllabus-selected` ? syllabus : null,
  schoolCode,
});

export const isLearningResultLessonsSkipped = ({ term, grade, subject, syllabus }, schoolCode) =>
  !term || !grade || !subject || !schoolCode || !syllabus || syllabus === `all-syllabus-selected`;

const AttendanceFilterData = props => {
  const {
    children,
    initialValue,
    schoolCode,
    isShowClassFilter = true,
    // Learning Result page: source lessons (with real Databricks lessonId) from
    // foLearningResultLessons instead of the MySQL sectionCode-based foLessons.
    useLearningResultLessons = false,
  } = props;

  const [term, setTerm] = useState(null);
  const [grade, setGrade] = useState(null);
  const [classNo, setClassNo] = useState(null);
  const [subject, setSubject] = useState(null);
  const [syllabus, setSyllabus] = useState(null);
  const [lesson, setLesson] = useState(null);

  useEffect(() => {
    if (initialValue) {
      updateValue(initialValue);
    }
  }, [initialValue]);

  const updateValue = ({ term, grade, classNo, subject, syllabus, lesson }) => {
    setTerm(term);
    setGrade(grade);
    setClassNo(classNo);
    setSubject(subject);
    setSyllabus(syllabus);
    setLesson(lesson);
  };

  const { data: termList } = useQuery(getSchoolTerms, {
    variables: { schoolCode },
    skip: !schoolCode,
    notifyOnNetworkStatusChange: true,
  });
  const { data: gradeList } = useQuery(getGrades, {
    variables: { schoolTermId: term, schoolCode },
    skip: !term || !schoolCode,
    notifyOnNetworkStatusChange: true,
  });

  const { data: classList } = useQuery(getClassNos, {
    variables: {
      schoolTermId: term,
      schoolGradeId: grade !== `all-grade-selected` ? grade : null,
      schoolCode,
    },
    skip: !term || !grade || !schoolCode,
    notifyOnNetworkStatusChange: true,
  });

  const { loading: subjectLoading, error: subjectError, data: subjectList } = useQuery(
    getSubjects,
    {
      variables: {
        schoolTermId: term,
        schoolGradeId: grade !== `all-grade-selected` ? grade : null,
        classNo: classNo !== `all-class-selected` && isShowClassFilter ? classNo : null,
        schoolCode,
      },
      skip: !term || !grade || !classNo || !schoolCode,
      notifyOnNetworkStatusChange: true,
    }
  );

  const { loading: syllabusLoading, error: syllabusError, data: syllabusList } = useQuery(
    getSyllabuses,
    {
      variables: {
        schoolTermId: term,
        schoolGradeId: grade !== `all-grade-selected` ? grade : null,
        classNo: classNo !== `all-class-selected` && isShowClassFilter ? classNo : null,
        subjectId: subject,
        schoolCode,
      },
      skip: !term || !grade || !classNo || !subject || !schoolCode,
      notifyOnNetworkStatusChange: true,
    }
  );
  const findSelectedCourse = syllabusList?.foCourses?.courses?.find(
    _syllabus => _syllabus.courseCode === syllabus
  );
  const mode =
    syllabusList?.foCourses?.mode ?? findSelectedCourse?.mode ?? SYLLABUS_MODE.TRADITIONAL;

  const courseCode = syllabus !== `all-syllabus-selected` ? syllabus : null;

  const { data: sectionLessonList } = useQuery(getLessons, {
    variables: { courseCode },
    skip: useLearningResultLessons || !syllabus || syllabus === `all-syllabus-selected`,
    notifyOnNetworkStatusChange: true,
  });

  const { loading: learningResultLessonLoading, data: learningResultLessonList } = useQuery(
    getLearningResultLessons,
    {
      variables: toLearningResultLessonsVariables({ term, grade, subject, syllabus }, schoolCode),
      skip:
        !useLearningResultLessons ||
        isLearningResultLessonsSkipped({ term, grade, subject, syllabus }, schoolCode),
      notifyOnNetworkStatusChange: true,
    }
  );

  // Normalize both sources to the shape AttendanceFilter expects
  // ({ foLessons: { lessons: [{ sectionCode, sectionName }] } }). For the
  // Learning Result page, sectionCode carries the Databricks lessonId.
  // It is undefined while loading, the same as foLessons, so the selected lesson
  // is not looked up in an empty list.
  const lessonList = useLearningResultLessons
    ? learningResultLessonLoading
      ? undefined
      : {
          foLessons: {
            lessons: (learningResultLessonList?.foLearningResultLessons ?? []).map(
              ({ lessonId, lessonName }) => ({
                sectionCode: lessonId,
                sectionName: lessonName,
              })
            ),
          },
        }
    : sectionLessonList;

  return children({
    updateValue,
    value: { term, grade, classNo, subject, syllabus, lesson, mode },
    list: {
      termList,
      gradeList,
      classList,
      subjectList,
      syllabusList,
      lessonList,
    },
  });
};

export default AttendanceFilterData;
